import { PRODUCT_MENU_GROUP_LABELS } from "@artisancode/api-types";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";

import type { SaleFormValues } from "./sale-schema";
import type { Product } from "@artisancode/api-types";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

interface SaleItemsTableProps {
  form: UseFormReturn<SaleFormValues>;
  fields: FieldArrayWithId<SaleFormValues, "lines", "id">[];
  products: Product[];
  /** Shelf stock of bought-in products; menus made fresh use bar ingredients instead. */
  stockByProductId?: Map<string, number>;
  grandTotal: number;
  selectProduct: (index: number, productId: string) => void;
  addLine: () => void;
  removeLine: (index: number) => void;
  locked?: boolean;
}

export function SaleItemsTable({
  form,
  fields,
  products,
  stockByProductId,
  grandTotal,
  selectProduct,
  addLine,
  removeLine,
  locked,
}: SaleItemsTableProps) {
  // Menus made fresh take their recipe ingredients from the bar, so shelf stock means nothing for them.
  const shelfStock = (product?: Product) =>
    product && product.sourcingType !== "produced"
      ? stockByProductId?.get(product.id)
      : undefined;

  return (
    <>
      <div className="flex items-center justify-between">
        <FormLabel>Produk</FormLabel>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addLine}
          disabled={locked}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Tambah Produk
        </Button>
      </div>

      <div className="hidden gap-2 px-2 text-xs text-muted-foreground sm:flex">
        <span className="flex-1">Produk</span>
        <span className="w-16">Qty</span>
        <span className="w-28">Harga Jual</span>
        <span className="w-9 shrink-0" />
      </div>

      {locked && (
        <p className="text-xs text-muted-foreground">
          Sudah ada pembayaran di Piutang — item tidak bisa diubah.
        </p>
      )}

      <fieldset
        disabled={locked}
        className="max-h-72 space-y-2.5 overflow-y-auto rounded-md border p-2 md:max-h-none md:flex-1"
      >
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-2.5 sm:flex-row sm:items-start sm:gap-2 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0"
          >
            <div className="flex items-center gap-2 sm:flex-1">
              <div className="flex-1">
                <FormField
                  control={form.control}
                  name={`lines.${index}.productId`}
                  render={({ field: f }) => {
                    const selectedProductId = f.value;
                    const selectedProduct = products.find(
                      (p) => p.id === selectedProductId,
                    );
                    const onHand = shelfStock(selectedProduct);
                    const requestedQty = form.watch(`lines.${index}.qty`) || 0;

                    return (
                      <FormItem>
                        <SearchableSelect
                          value={f.value}
                          onChange={(v) => selectProduct(index, v)}
                          options={products.map((p) => {
                            const stock = shelfStock(p);
                            const stockLabel =
                              stock !== undefined ? ` (Stok: ${stock})` : "";
                            return {
                              value: p.id,
                              label: `${p.name}${stockLabel}`,
                              group: PRODUCT_MENU_GROUP_LABELS[p.menuGroup],
                            };
                          })}
                          placeholder="Cari produk..."
                        />
                        {selectedProductId && onHand !== undefined && (
                          <div className="mt-1">
                            {onHand <= 0 ? (
                              <div className="flex flex-wrap items-center justify-between gap-1 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
                                <span className="flex items-center gap-1 font-medium">
                                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                  Stok fisik toko habis (0)
                                </span>
                              </div>
                            ) : requestedQty > onHand ? (
                              <div className="flex flex-wrap items-center justify-between gap-1 rounded bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
                                <span className="flex items-center gap-1 font-medium">
                                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                  Qty ({requestedQty}) melebihi stok toko (
                                  {onHand})
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                <span>Stok toko:</span>
                                <Badge
                                  variant="outline"
                                  className="h-4 px-1 text-[10px] font-mono"
                                >
                                  {onHand} unit
                                </Badge>
                              </div>
                            )}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-muted-foreground hover:text-destructive sm:hidden"
                disabled={fields.length === 1}
                onClick={() => removeLine(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <FormField
                control={form.control}
                name={`lines.${index}.qty`}
                render={({ field: f }) => (
                  <FormItem className="w-24 sm:w-16">
                    <span className="text-[10px] text-muted-foreground sm:hidden">
                      Qty
                    </span>
                    <FormControl>
                      <NumberInput
                        placeholder="Qty"
                        value={f.value ?? 1}
                        onChange={(value) => f.onChange(value || 1)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`lines.${index}.sellingPrice`}
                render={({ field: f }) => (
                  <FormItem className="flex-1 sm:w-28 sm:flex-initial">
                    <span className="text-[10px] text-muted-foreground sm:hidden">
                      Harga
                    </span>
                    <FormControl>
                      <NumberInput
                        placeholder="Harga"
                        value={f.value ?? 0}
                        onChange={f.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="hidden shrink-0 sm:inline-flex"
                disabled={fields.length === 1}
                onClick={() => removeLine(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </fieldset>
      <p className="text-sm text-muted-foreground">
        Total:{" "}
        <span className="font-medium text-foreground">
          {(grandTotal || 0).toLocaleString("id-ID")}
        </span>
      </p>
    </>
  );
}
